import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../utils/AuthContext';
import { auth } from '../utils/firebase';
import { inviteAcceptErrorMessage } from '../utils/inviteAcceptForm';
import Logo from '../components/ui/Logo';
import '../styles/Login.css';

const LOGIN_WATERMARK_URL = `${process.env.PUBLIC_URL || ''}/assets/logo/LCTF%20Profile.png`;

const InviteAcceptPage: React.FC = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const { signInWithGoogle, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('pending');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [inviteLoaded, setInviteLoaded] = useState(false);
  const [inviteKind, setInviteKind] = useState<'architect' | 'subcontractor'>('architect');

  useEffect(() => {
    if (!inviteId) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<
          { inviteId: string },
          { email: string; name: string; status: string; inviteKind?: 'architect' | 'subcontractor' }
        >(
          getFunctions(undefined, 'us-central1'),
          'getCrmInvitePublic',
        );
        const { data } = await fn({ inviteId });
        if (cancelled) return;
        setEmail(data.email || '');
        setName(data.name || '');
        setStatus(data.status || 'pending');
        setInviteKind(data.inviteKind === 'subcontractor' ? 'subcontractor' : 'architect');
        setInviteLoaded(true);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Invite not found.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteId]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'architect') {
      navigate('/architect-portal', { replace: true });
    }
    if (isAuthenticated && user?.role === 'subcontractor') {
      navigate('/briefings', { replace: true });
    }
  }, [isAuthenticated, user?.role, navigate]);

  const finishAccept = async (newPassword?: string) => {
    const fnName = inviteKind === 'subcontractor' ? 'acceptTradeBriefingInvite' : 'acceptCrmInvite';
    const fn = httpsCallable<
      { inviteId?: string; newPassword?: string },
      { attached?: boolean; role?: string }
    >(
      getFunctions(undefined, 'us-central1'),
      fnName,
    );
    const { data } = await fn({
      inviteId,
      ...(newPassword ? { newPassword } : {}),
    });
    if (!data?.attached) {
      throw new Error('This invite is not pending for the signed-in email.');
    }
    window.location.replace(data.role === 'subcontractor' ? '/briefings' : '/architect-portal');
  };

  const handlePasswordAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!password.trim()) {
      setError('Enter the temporary password from your invite email.');
      return;
    }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password.trim());
      await finishAccept();
    } catch (err) {
      setError(inviteAcceptErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      await finishAccept();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div
        className="login-container-watermark"
        aria-hidden
        style={{ backgroundImage: `url("${LOGIN_WATERMARK_URL}")` }}
      />
      <div className="login-card">
        <div className="login-header">
          <div className="login-header-logo" aria-hidden>
            <Logo size={72} />
          </div>
          <h1>{inviteKind === 'subcontractor' ? 'Trade briefing invite' : 'Architect invite'}</h1>
          <p>{name ? `Welcome, ${name}` : 'Leiper\'s Creek Timber Frames'}</p>
        </div>

        {loadError && <div className="error-message">{loadError}</div>}
        {!loadError && !inviteLoaded && <p>Loading invite…</p>}
        {!loadError && status && status !== 'pending' && (
          <p>This invite is no longer pending. Sign in on the login page.</p>
        )}

        {!loadError && inviteLoaded && status === 'pending' && (
          <>
            <button type="button" className="google-button" disabled={loading} onClick={handleGoogle}>
              Continue with Google
            </button>
            <div className="divider"><span>or sign in with password</span></div>
            <p style={{ fontSize: 13, color: '#6B6960', margin: '0 0 12px' }}>
              Use the temporary password from your invite email. Ignore a setup link that says it expired.
            </p>
            <form onSubmit={handlePasswordAccept} className="login-form">
              <div className="form-group">
                <label htmlFor="invite-email">Email</label>
                <input id="invite-email" type="email" value={email} readOnly />
              </div>
              <div className="form-group">
                <label htmlFor="invite-password">Temporary password</label>
                <input
                  id="invite-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Please wait…' : 'Sign in and continue'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
